import React, { useState, useCallback } from 'react';
import UrlMultiInput from '../shared/UrlMultiInput';

const FIELD_TYPES = [
  { value: 'string', label: 'Text' },
  { value: 'array', label: 'List (e.g. doctor names)' },
];

const PRESETS = [
  {
    id: 'clear',
    name: 'Clear',
    description: 'Reset instruction and output fields to empty',
    instruction: '',
    fields: [],
  },
  {
    id: 'clid7715-doctor-service-list',
    name: 'CLID7715 – Service Page to Doctor - Service List',
    description: 'Finding each doctor service list via Service Pages',
    instruction: 'From each service page, extract the service name and the list of doctors linked to that service. For multi-language rows, include service (English) and service_zh_HK (Chinese). service_doctor_list should be an array of doctor names.',
    fields: [
      { name: 'service', description: 'Name of the service', required: true, type: 'string' },
      { name: 'service_zh_HK', description: 'Service name in Chinese (if available)', required: false, type: 'string' },
      { name: 'service_doctor_list', description: 'List of doctor names linked to this service', required: true, type: 'array' },
    ],
  },
  {
    id: 'doctor-pages',
    name: 'CLID7715 - Doctor Page to Doctor List',
    description: 'Extract doctor info; select zh_HK for primary name',
    selectLanguages: ['en_US', 'zh_HK'],
    instruction: 'From each doctor page, extract the following. Use zh_HK as the primary name (ensure zh_HK is selected in languages). primary_name should be the doctor name in zh_HK. specialty is the doctor specialty. service_provided is a list of services; include both Chinese and English for each (e.g. 洗牙 Teeth cleaning). gender if available. type is always "doctor". For meta_data, output a JSON object with i18n name structure: {"i18n": {"name": {"en_US": "", "zh_HK": "", "zh_CN": ""}}}. Fill in the name values for each language if available on the page. doctor_schedule: JSON object with keys "0"(Mon), "1"(Tue), "2"(Wed), "3"(Thu), "4"(Fri), "5"(Sat), "6"(Sun), "PH"(Public Holiday); each value is array of {"from": "HH:MM", "to": "HH:MM"}.',
    fields: [
      { name: 'primary_name', description: 'Primary name in zh_HK', required: true, type: 'string' },
      { name: 'specialty', description: 'Doctor specialty', required: true, type: 'string' },
      { name: 'service_provided', description: 'List of services provided (both Chinese and English, e.g. 洗牙 Teeth cleaning)', required: true, type: 'array' },
      { name: 'gender', description: 'Gender', required: false, type: 'string' },
      { name: 'type', description: 'Fixed value: doctor', required: true, type: 'string' },
      { name: 'meta_data', description: 'JSON e.g. {"i18n": {"name": {"en_US": "", "zh_HK": "", "zh_CN": ""}}}', required: true, type: 'string' },
      { name: 'doctor_schedule', description: 'JSON: keys "0"(Mon),"1"(Tue),"2"(Wed),"3"(Thu),"4"(Fri),"5"(Sat),"6"(Sun),"PH"(Public Holiday); values array of {"from":"HH:MM","to":"HH:MM"}', required: false, type: 'string' },
    ],
  },
];

const formatDisplayUrl = (url) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

const Step1_Configure = ({
  urlPool,
  setUrlPool,
  urlRows,
  setUrlRows,
  urls,
  setUrls,
  languages,
  setLanguages,
  selectedLanguages,
  setSelectedLanguages,
  instruction,
  setInstruction,
  fields,
  setFields,
  onNext,
  error,
}) => {
  const [newLangCode, setNewLangCode] = useState('');

  const isMultiMode = selectedLanguages.length >= 2;
  const selectedLangsList = languages.filter((l) => selectedLanguages.includes(l.code));

  const addField = useCallback(() => {
    setFields((prev) => [...prev, { name: '', description: '', required: true, type: 'string' }]);
  }, [setFields]);

  const updateField = useCallback((index, key, value) => {
    setFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  }, [setFields]);

  const removeField = useCallback((index) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  }, [setFields]);

  const toggleLanguage = useCallback((code) => {
    const isAdding = !selectedLanguages.includes(code);
    if (isAdding) {
      setUrlRows((rows) => {
        const withNewCol = rows.map((row) => ({ ...row, [code]: row[code] || '' }));
        if (withNewCol.length === 0) {
          const newRow = {};
          [...selectedLanguages, code].forEach((c) => (newRow[c] = ''));
          return [newRow];
        }
        return withNewCol;
      });
    }
    setSelectedLanguages((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }, [selectedLanguages, setSelectedLanguages, setUrlRows]);

  const addCustomLanguage = useCallback(() => {
    const code = newLangCode.trim();
    if (!code) return;
    if (languages.some((l) => l.code === code)) return;
    setLanguages((prev) => [...prev, { code, label: code }]);
    setNewLangCode('');
  }, [newLangCode, languages, setLanguages]);

  const removeLanguage = useCallback((code) => {
    setLanguages((prev) => prev.filter((l) => l.code !== code));
    setSelectedLanguages((prev) => prev.filter((c) => c !== code));
    setUrlRows((prev) =>
      prev.map((row) => {
        const next = { ...row };
        delete next[code];
        return next;
      })
    );
  }, [setLanguages, setSelectedLanguages, setUrlRows]);

  const removeFromPool = useCallback((index) => {
    setUrlPool((prev) => prev.filter((_, i) => i !== index));
  }, [setUrlPool]);

  const clearPool = useCallback(() => setUrlPool([]), [setUrlPool]);

  const removeUrlAtIndex = useCallback((index) => {
    setUrls((prev) => prev.filter((_, i) => i !== index));
  }, [setUrls]);

  const clearUrls = useCallback(() => setUrls([]), [setUrls]);

  const addRow = useCallback(() => {
    const empty = {};
    selectedLangsList.forEach((l) => (empty[l.code] = ''));
    setUrlRows((prev) => [...prev, empty]);
  }, [selectedLangsList, setUrlRows]);

  const removeRow = useCallback((rowIndex) => {
    setUrlRows((prev) => prev.filter((_, i) => i !== rowIndex));
  }, [setUrlRows]);

  const setCellUrl = useCallback((rowIndex, langCode, url) => {
    setUrlRows((prev) => {
      const next = prev.map((r, i) =>
        i === rowIndex ? { ...r, [langCode]: url } : r
      );
      return next;
    });
  }, [setUrlRows]);

  const handleDragStart = (e, url) => {
    e.dataTransfer.setData('text/plain', url);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, rowIndex, langCode) => {
    e.preventDefault();
    const url = e.dataTransfer.getData('text/plain');
    if (url) {
      setCellUrl(rowIndex, langCode, url);
      setUrlPool((prev) => prev.filter((u) => u !== url));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const removeFromCell = (rowIndex, langCode) => {
    const row = urlRows[rowIndex];
    const url = row[langCode];
    if (url) {
      setCellUrl(rowIndex, langCode, '');
      setUrlPool((prev) => [...prev, url]);
    }
  };

  const getAllUrls = useCallback(() => {
    if (!isMultiMode) return urls;
    const set = new Set();
    urlRows.forEach((row) => {
      selectedLangsList.forEach((l) => {
        const u = row[l.code];
        if (u) set.add(u);
      });
    });
    return Array.from(set);
  }, [isMultiMode, urls, urlRows, selectedLangsList]);

  const allUrls = getAllUrls();
  const validInstruction = (instruction || '').trim().length > 0;
  const validFields = fields.length > 0 && fields.every((f) => (f.name || '').trim().length > 0 && (f.description || '').trim().length > 0);
  const canNext = allUrls.length > 0 && validInstruction && validFields;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 1: Configure extraction</h2>
        <p className="text-gray-600">Select languages, add URLs, then define output fields.</p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-3">Languages</label>
        <div className="space-y-2 mb-4">
          {languages.map((l) => (
            <div key={l.code} className="flex items-center gap-3">
              <input
                type="checkbox"
                id={`lang-${l.code}`}
                checked={selectedLanguages.includes(l.code)}
                onChange={() => toggleLanguage(l.code)}
                className="rounded border-gray-300"
              />
              <label htmlFor={`lang-${l.code}`} className="text-sm text-gray-700">
                {l.code}
              </label>
              <button
                type="button"
                onClick={() => removeLanguage(l.code)}
                className="text-red-500 hover:text-red-700 text-xs"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={newLangCode}
            onChange={(e) => setNewLangCode(e.target.value)}
            placeholder="Code (e.g. zh_CN)"
            className="px-2 py-1.5 border border-gray-300 rounded text-sm w-28"
          />
          <button
            type="button"
            onClick={addCustomLanguage}
            disabled={!newLangCode.trim()}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            Add language
          </button>
        </div>
        {isMultiMode && (
          <p className="text-xs text-blue-600 mt-2">
            Multi-language mode: {selectedLanguages.length} selected. Use table below to pair URLs.
          </p>
        )}
      </div>

      {!isMultiMode ? (
        <div className="bg-gray-50 p-6 rounded-lg space-y-4">
          <UrlMultiInput
            urls={urls}
            setUrls={setUrls}
            rows={3}
            placeholder="https://example.com/page-1, ... (press Enter after each URL)"
            label="Page URLs (one per line or comma-separated)"
            description="Press Enter after each URL."
          />
          {urls.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{urls.length} URL{urls.length !== 1 ? 's' : ''} ready</span>
                <button type="button" onClick={clearUrls} className="text-xs text-red-600 hover:text-red-700 font-medium">
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {urls.map((url, index) => (
                  <span
                    key={`${url}-${index}`}
                    className="inline-flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded-full shadow-sm"
                  >
                    <span className="max-w-xs truncate mr-2">{formatDisplayUrl(url)}</span>
                    <button type="button" onClick={() => removeUrlAtIndex(index)} className="text-gray-500 hover:text-red-600">
                      x
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="bg-gray-50 p-6 rounded-lg space-y-4">
            <UrlMultiInput
              urls={urlPool}
              setUrls={setUrlPool}
              rows={2}
              placeholder="Paste URLs here, then drag to table cells..."
              label="URLs ready to process"
              description="Add URLs. Drag into table cells to pair same service in different languages."
            />
            {urlPool.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{urlPool.length} URL{urlPool.length !== 1 ? 's' : ''} in pool</span>
                  <button type="button" onClick={clearPool} className="text-xs text-red-600 hover:text-red-700 font-medium">
                    Clear pool
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {urlPool.map((url, index) => (
                    <span
                      key={`${url}-${index}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, url)}
                      className="inline-flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded-full shadow-sm cursor-grab active:cursor-grabbing"
                    >
                      <span className="max-w-xs truncate mr-2">{formatDisplayUrl(url)}</span>
                      <button type="button" onClick={() => removeFromPool(index)} className="text-gray-500 hover:text-red-600">
                        x
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 p-6 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-3">URL table (same row = same service)</label>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 w-12">#</th>
                    {selectedLangsList.map((l) => (
                      <th key={l.code} className="px-3 py-2 text-left font-medium text-gray-700 min-w-[200px]">
                        {l.code}
                      </th>
                    ))}
                    <th className="px-3 py-2 w-16" />
                  </tr>
                </thead>
                <tbody>
                  {urlRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="border-t border-gray-200 bg-white hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{rowIndex + 1}</td>
                      {selectedLangsList.map((l) => {
                        const url = row[l.code] || '';
                        return (
                          <td key={l.code} className="px-3 py-2">
                            <div
                              onDrop={(e) => handleDrop(e, rowIndex, l.code)}
                              onDragOver={handleDragOver}
                              className={`min-h-[40px] p-2 border rounded ${
                                url ? 'bg-green-50 border-green-200' : 'border-dashed border-gray-300 bg-gray-50'
                              }`}
                            >
                              {url ? (
                                <span className="flex items-center justify-between gap-1">
                                  <span className="truncate text-gray-800" title={url}>
                                    {formatDisplayUrl(url)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removeFromCell(rowIndex, l.code)}
                                    className="text-red-500 hover:text-red-700 shrink-0"
                                  >
                                    x
                                  </button>
                                </span>
                              ) : (
                                <input
                                  type="text"
                                  placeholder="Paste or drop URL"
                                  className="w-full text-xs bg-transparent border-none focus:outline-none focus:ring-0 placeholder-gray-400"
                                  onBlur={(e) => {
                                    const v = e.target.value.trim();
                                    if (v) {
                                      const normalized = /^https?:\/\//i.test(v) ? v : `https://${v}`;
                                      setCellUrl(rowIndex, l.code, normalized);
                                      e.target.value = '';
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.target.blur();
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeRow(rowIndex)}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={addRow} className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
              + Add row
            </button>
          </div>
        </>
      )}

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">Instruction (prompt)</label>
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          rows={4}
          placeholder="Describe what to extract. For multi-language, ask for fields per language (e.g. service, service_zh_HK)."
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
        />
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">Output fields</label>
          <button type="button" onClick={addField} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            + Add field
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">Use type &quot;List&quot; for arrays. For bilingual add fields like service_zh_HK.</p>
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div key={index} className="flex flex-wrap items-start gap-2 p-3 bg-white border border-gray-200 rounded-md">
              <input
                type="text"
                value={field.name}
                onChange={(e) => updateField(index, 'name', e.target.value)}
                placeholder="Field name (e.g. service)"
                className="flex-1 min-w-[120px] px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
              <input
                type="text"
                value={field.description}
                onChange={(e) => updateField(index, 'description', e.target.value)}
                placeholder="Description"
                className="flex-1 min-w-[160px] px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
              <select
                value={field.type || 'string'}
                onChange={(e) => updateField(index, 'type', e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                {FIELD_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={field.required !== false}
                  onChange={(e) => updateField(index, 'required', e.target.checked)}
                />
                Required
              </label>
              <button type="button" onClick={() => removeField(index)} className="text-red-600 hover:text-red-700 text-sm">
                Remove
              </button>
            </div>
          ))}
        </div>
        {fields.length === 0 && (
          <button type="button" onClick={addField} className="mt-2 text-sm text-blue-600 hover:text-blue-700">
            Add first field
          </button>
        )}
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-3">Example templates</label>
        <p className="text-xs text-gray-500 mb-3">Click a block to apply its instruction and output fields.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                setInstruction(preset.instruction);
                setFields(preset.fields.map((f) => ({ ...f })));
                if (preset.selectLanguages?.length) {
                  setSelectedLanguages((prev) => {
                    const next = new Set([...prev, ...preset.selectLanguages]);
                    return [...next];
                  });
                }
              }}
              className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
            >
              <div className="font-medium text-gray-900">{preset.name}</div>
              <div className="text-xs text-gray-600 mt-1">{preset.description}</div>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!canNext}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Next: Scrape ({allUrls.length} URLs)
        </button>
      </div>
    </div>
  );
};

export default Step1_Configure;
